/**
 * Spell accented letters without their marks ("Café" → "Cafe"): the NFKD form
 * splits a letter from its combining marks, which are then dropped.
 */
function stripMarks(text: string): string {
  return text.normalize("NFKD").replace(/\p{M}+/gu, "");
}

/**
 * URL-friendly slug: accents transliterated, lowercase, non-alphanumeric runs
 * collapsed to single hyphens, no leading/trailing hyphen. Shared by
 * workspace-handle generation on the API and slug/filename construction in
 * the client.
 */
export function toUrlFriendly(name: string | null | undefined): string {
  if (!name) return "";

  return stripMarks(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * The typing form of `toUrlFriendly`: the same slug, except one trailing
 * hyphen is kept so a user can type "my-co" without the separator vanishing
 * under them. Runs of hyphens collapse as they are typed, so the field never
 * shows a slug the submit would change.
 */
export function toUrlFriendlyInput(value: string): string {
  return stripMarks(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-/, "");
}

/**
 * Shorten text to at most `max` characters, marking the cut with an ellipsis.
 *
 * `max` is the ceiling on the RESULT, so the ellipsis is paid for out of the
 * budget rather than added past it — `truncate("abcdefghij", 8)` is eight
 * characters, not nine. That is the whole reason this is worth stating once:
 * every caller that gets it wrong overflows the limit it was written to respect,
 * which for a hard API ceiling means the payload is rejected rather than
 * trimmed.
 *
 * Counts UTF-16 code units, which is what a length limit expressed in
 * "characters" almost always means at an API boundary. An emoji or a combining
 * mark can therefore be split by a cut that lands inside it.
 */
export function truncate(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

/**
 * Whether two strings are the same text as a READER would judge it: surrounding
 * whitespace and capitalization do not count.
 *
 * For deciding whether a value is worth showing twice — a heading beside the
 * field it fell back to, a typed label beside a stored one. Not an identity
 * check: use `===` for ids, tokens and anything a machine compares.
 *
 * What it does NOT fold, because folding is a guess about the caller's data:
 * internal whitespace runs, punctuation, accents, and Unicode forms that
 * `normalize()` would reconcile. `lowercase` is locale-independent, so a Turkish
 * dotless ı does not match an I. Two spellings of one address are still two
 * different texts here.
 */
export function sameText(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}
